/**
 * The filter bar's currency, channel and objective, applied on the client
 * (spec §2.2: no read). FR-X-02: every number is in ONE currency, and spend
 * on accounts of unknown currency is in none (R8). Pure.
 *
 * @module services/overview/overviewFilters
 */
import { isChannelShown } from '@/services/channelFilter';
import type { CurrencyChoice, FactRow, ObjectiveFilter } from './overviewTypes';

/** Ranking only: a row with no spend has none to rank by. Never shown. */
const rankSpend = (r: Pick<FactRow, 'spend'>): number => r.spend ?? 0;

/** Any rows with a currency and a spend: D1's facts, and /channels' facts or ads (the ads when its facts fail). */
export const currencyChoiceOf = (
  rows: ReadonlyArray<Pick<FactRow, 'currency' | 'spend'>>,
  requested: string | null,
): CurrencyChoice => {
  // Built locally and never shared.
  const byCurrency = new Map<string, number>();
  rows.forEach((r) => {
    if (r.currency !== null && rankSpend(r) > 0) byCurrency.set(r.currency, (byCurrency.get(r.currency) ?? 0) + rankSpend(r));
  });
  const currencies = [...byCurrency.entries()].sort(([a, x], [b, y]) => y - x || a.localeCompare(b)).map(([c]) => c);
  const currency = requested !== null && currencies.includes(requested) ? requested : currencies[0] ?? null;
  return { currencies, currency, unknownExcluded: rows.some((r) => r.currency === null && rankSpend(r) > 0) };
};

export interface FactFilters {
  /** Raw channel ids; [] = every channel. */
  channels: readonly string[];
  objective: ObjectiveFilter;
  /** The chosen currency; null = none known, so nothing qualifies. */
  currency: string | null;
}

export const filterFacts = (rows: readonly FactRow[], f: FactFilters): FactRow[] => {
  if (f.currency === null) return [];
  const selected = new Set(f.channels);
  return rows.filter((r) =>
    r.currency === f.currency
    && isChannelShown(selected, r.platform)
    && (f.objective === 'all' || r.objective === f.objective));
};
